import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import type { AnomalyGroup, AnomalyState } from "@shared/types/domain";
import { useState } from "react";
import { useOccurrenceColumnWidths } from "../hooks/useOccurrenceColumnWidths";
import { OccurrenceHeaderRow, OccurrenceRow } from "./KksOccurrenceRow";

interface Props {
  data: AnomalyState | undefined;
  isLoading: boolean;
  onRefresh: () => void;
  isRefreshing: boolean;
  onNavigate: (
    plantId: string,
    systemId: string,
    pidId: string,
    kksId: string,
  ) => void;
}

export default function AnomalyList({
  data,
  isLoading,
  onRefresh,
  isRefreshing,
  onNavigate,
}: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { colWidths, resizeColumn, commitColumnWidths } =
    useOccurrenceColumnWidths("anomalies");

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const formatRelativeTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const groups: AnomalyGroup[] = data
    ? [...data.groups].sort((a, b) =>
        a.normalized_text.localeCompare(b.normalized_text),
      )
    : [];

  const busy = data?.scheduled || data?.running;

  const allExpanded = groups.length > 0 && expanded.size >= groups.length;
  const toggleAll = () =>
    setExpanded(
      allExpanded ? new Set() : new Set(groups.map((g) => g.normalized_text)),
    );

  const anomalyExplanation = (
    <Box sx={{ maxWidth: 320 }}>
      An anomaly group collects <strong>verified</strong> KKS that share the
      same normalized text but disagree on their <strong>SPOF</strong> flag or{" "}
      <strong>impact</strong> value. Only texts with at least two occurrences
      and at least one such disagreement are listed here, so you can align the
      SPOF/impact of identical KKS across P&IDs.
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.2,
          borderBottom: "1px solid #DEE3EA",
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            flex: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          Anomaly Groups
          {data && (
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 1, color: "text.secondary" }}
            >
              ({data.group_count})
            </Typography>
          )}
          <Tooltip title={anomalyExplanation} arrow>
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ ml: 0.6, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>

        {data?.last_run_at && (
          <Tooltip title={new Date(data.last_run_at).toLocaleString()}>
            <Chip
              label={`Last updated ${formatRelativeTime(data.last_run_at)}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 22 }}
            />
          </Tooltip>
        )}

        {data?.scheduled && (
          <Chip
            label="Refresh scheduled"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontSize: "0.7rem", height: 22 }}
          />
        )}
        {data?.running && (
          <Chip
            label="Refreshing…"
            size="small"
            color="info"
            variant="outlined"
            icon={<CircularProgress size={10} />}
            sx={{ fontSize: "0.7rem", height: 22 }}
          />
        )}

        <Tooltip title={allExpanded ? "Collapse all" : "Expand all"}>
          <span>
            <IconButton
              size="small"
              onClick={toggleAll}
              disabled={groups.length === 0}
            >
              {allExpanded ? (
                <UnfoldLessIcon fontSize="small" />
              ) : (
                <UnfoldMoreIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Refresh anomaly data">
          <span>
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={isRefreshing || !!data?.scheduled}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!isLoading && groups.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 3, textAlign: "center" }}
          >
            {busy ? "Computing anomalies…" : "No anomalies found."}
          </Typography>
        )}

        {groups.length > 0 && (
          <OccurrenceHeaderRow
            variant="anomalies"
            colWidths={colWidths}
            onResize={resizeColumn}
            onResizeEnd={commitColumnWidths}
          />
        )}

        {groups.map((group) => {
          const key = group.normalized_text;
          const isOpen = expanded.has(key);
          return (
            <Box key={key} sx={{ borderBottom: "1px solid #F0F2F5" }}>
              {/* Group row */}
              <Box
                onClick={() => toggle(key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle(key);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.8,
                  cursor: "pointer",
                  bgcolor: "#FAFBFC",
                  "&:hover": { bgcolor: "#F0F4F8" },
                }}
              >
                <IconButton
                  size="small"
                  sx={{ p: 0 }}
                  tabIndex={-1}
                  aria-hidden
                >
                  {isOpen ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, flex: 1, fontFamily: "monospace" }}
                >
                  {group.normalized_text}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.occurrences.length} occurrences
                </Typography>
              </Box>

              {/* Occurrence rows */}
              {isOpen &&
                group.occurrences.map((occ, i) => (
                  <OccurrenceRow
                    key={occ.kks_id ?? i}
                    occurrence={occ}
                    colWidths={colWidths}
                    variant="anomalies"
                    onClick={() =>
                      onNavigate(
                        occ.plant_id,
                        occ.system_id,
                        occ.pid_id,
                        occ.kks_id,
                      )
                    }
                  />
                ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
